const BLACKLIST_PATH = "/api/v1/notification-blacklist";

export interface NotificationBlacklistApiOptions {
  authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>;
  /** Root directory base URL (e.g. "https://a3s-int.visioni.network") — NOT a realm URL. */
  directoryBaseUrl: string | (() => Promise<string>);
}

export type BlacklistCheckResult = { blacklisted: boolean };
export type BlacklistListResult = { items: { scope: string; createdAt: number }[] };

export interface NotificationBlacklistApi {
  check(tenantId: string, siteId: string, assetCameraKey: string): Promise<BlacklistCheckResult>;
  list(tenantId?: string): Promise<BlacklistListResult>;
  add(tenantId: string, siteId: string, assetCameraKey: string): Promise<void>;
  remove(tenantId: string, siteId: string, assetCameraKey: string): Promise<void>;
}

export function createNotificationBlacklistApi({
  authenticatedFetch,
  directoryBaseUrl,
}: NotificationBlacklistApiOptions): NotificationBlacklistApi {
  async function baseUrl(): Promise<string> {
    const url = typeof directoryBaseUrl === "function" ? await directoryBaseUrl() : directoryBaseUrl;
    return `${url}${BLACKLIST_PATH}`;
  }

  return {
    async check(tenantId, siteId, assetCameraKey) {
      const url = `${await baseUrl()}?tenantId=${encodeURIComponent(tenantId)}&siteId=${encodeURIComponent(siteId)}&assetCameraKey=${encodeURIComponent(assetCameraKey)}`;
      const response = await authenticatedFetch(url, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Failed to check blacklist: ${response.status.toString()}`);
      }
      return (await response.json()) as BlacklistCheckResult;
    },

    async list(tenantId) {
      const params = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : "";
      const url = `${await baseUrl()}${params}`;
      const response = await authenticatedFetch(url, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Failed to list blacklist: ${response.status.toString()}`);
      }
      return (await response.json()) as BlacklistListResult;
    },

    async add(tenantId, siteId, assetCameraKey) {
      const url = await baseUrl();
      const response = await authenticatedFetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId, siteId, assetCameraKey }),
      });
      if (!response.ok) {
        throw new Error(`Failed to blacklist camera: ${response.status.toString()}`);
      }
    },

    async remove(tenantId, siteId, assetCameraKey) {
      const url = await baseUrl();
      const response = await authenticatedFetch(url, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId, siteId, assetCameraKey }),
      });
      if (!response.ok) {
        throw new Error(`Failed to un-blacklist camera: ${response.status.toString()}`);
      }
    },
  };
}
